import { Link } from 'react-router-dom';
import { Calendar, Users, Activity, ChevronRight } from 'lucide-react';
import { useAdminEvents } from '@/hooks/useEvents';
import { formatDateBrussels } from '@/lib/utils';

function StatCard({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`p-4 rounded-full ${color}`}>
                <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
                <div className="text-2xl font-bold text-gray-900">{value}</div>
                <div className="text-sm text-gray-500">{label}</div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    // We can reuse useEvents or create a specialized hook for stats
    const { data: events, isLoading } = useAdminEvents();

    const eventCount = events?.length || 0;
    const publishedCount = events?.filter(e => e.is_published)?.length || 0;

    return (
        <div>
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-8">Tableau de bord</h1>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <StatCard
                    icon={Calendar}
                    label="Événements total"
                    value={isLoading ? '...' : eventCount}
                    color="bg-primary-500"
                />
                <StatCard
                    icon={Activity}
                    label="Publiés"
                    value={isLoading ? '...' : publishedCount}
                    color="bg-green-500"
                />
                <StatCard
                    icon={Users}
                    label="Brouillons"
                    value={isLoading ? '...' : eventCount - publishedCount}
                    color="bg-orange-500"
                />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Activité récente</h2>
                    <Link to="/admin/events" className="text-primary-600 hover:underline text-sm font-medium">
                        Gérer les événements
                    </Link>
                </div>

                {/* Simple placeholder list */}
                <div className="space-y-4">
                    {!isLoading && events?.slice(0, 5).map(event => (
                        <Link
                            key={event.id}
                            to={`/admin/events/${event.id}/edit`}
                            className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg hover:bg-primary-50/80 border border-transparent hover:border-primary-100 transition-colors group"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 group-hover:text-primary-800">{event.title}</div>
                                <div className="text-sm text-gray-500 mt-0.5">
                                    {formatDateBrussels(event.date)} · {event.type}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <div className={`text-sm px-2 py-1 rounded ${event.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                                    {event.is_published ? 'Publié' : 'Brouillon'}
                                </div>
                                <ChevronRight size={18} className="text-gray-400 group-hover:text-primary-600" aria-hidden />
                            </div>
                        </Link>
                    ))}
                    {isLoading && <div className="text-gray-500 text-center py-4">Chargement...</div>}
                </div>
            </div>
        </div>
    );
}
